import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const COOKIE_CONSENT_KEY = "qlivar-cookie-consent";

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "accepted");
    setIsVisible(false);
  };

  const declineCookies = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "declined");
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-[65px] md:bottom-0 left-0 right-0 z-[100] cookie-consent-safe"
        >
          <div className="container max-w-4xl mx-auto px-4 pb-4">
            <div className="bg-background/95 backdrop-blur-md border border-border/50 shadow-2xl rounded-2xl p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 relative overflow-hidden">
              <div className="flex items-center gap-3 flex-1 w-full">
                <div className="p-2 rounded-full bg-primary/10 text-primary flex-shrink-0 hidden sm:flex">
                  <Cookie className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0 pr-6 sm:pr-0">
                  <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
                    <Cookie className="h-4 w-4 text-primary sm:hidden flex-shrink-0" />
                    <h3 className="font-semibold text-sm sm:text-base text-foreground truncate">We use cookies</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-tight">
                    We use cookies to improve your experience.{" "}
                    <Link to="/cookies" className="text-primary hover:underline whitespace-nowrap">
                      Policy
                    </Link>
                  </p>
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center justify-end gap-2 w-full sm:w-auto mt-1 sm:mt-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={declineCookies}
                  className="h-8 text-xs sm:text-sm px-3 flex-1 sm:flex-none"
                >
                  Decline
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={acceptCookies}
                  className="h-8 text-xs sm:text-sm px-4 flex-1 sm:flex-none bg-foreground text-background hover:bg-foreground/90 rounded-full"
                >
                  Accept All
                </Button>
              </div>
              <button
                onClick={declineCookies}
                className="absolute top-2 right-2 p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-full transition-colors sm:hidden"
                aria-label="Close"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
